import axios, { CancelToken, Method } from "axios";
import normalize from "json-api-normalizer";
import { get, omit, merge } from "lodash-es";

import { AuthService } from "Auth/services/AuthService";
import { loggerService } from "shared/services";
import { getHeaders, normalizePagination } from "shared/utils";
import { IJSONAPIError, IJSONAPIVialotion, IAPIError } from "shared/types";
import { API_CONTENT_TYPE } from "shared/constants";
import config from "config";
import { getImpersonateCookie } from "shared/utils/getImpersonateCookie";

interface IAPICallOption {
  noAuth?: boolean;
}

type IAPICallProps = {
  method?: Method;
  path: string;
  data?: any;
  headers?: { [key: string]: string };
  options?: IAPICallOption;
  cancelToken?: CancelToken;
  isJSON?: boolean;
  normalizeData?: boolean;
};

/*
  API Service to perform all API calls.
*/
export class APIService {
  private static getDefaultHeaders = async ({
    headers: extraHeaders,
    options,
  }: {
    headers?: {
      [key: string]: string;
    };
    options?: IAPICallOption;
  }) => {
    const headers: any = {
      ...getHeaders(),
      ...extraHeaders,
    };

    try {
      const impersonate = getImpersonateCookie();

      if (impersonate) {
        headers["X-Impersonate"] = impersonate;
      }

      if (!options?.noAuth) {
        const token = await AuthService.getJWTToken();
        if (token !== null) {
          headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (error) {
      // empty catch
    }

    return headers;
  };

  private static apiCall = ({
    method,
    path,
    data,
    headers,
    options,
    cancelToken,
    normalizeData = true,
  }: IAPICallProps): Promise<any> =>
    new Promise(async (resolve, reject) => {
      const url = path.startsWith("http") ? path : `${config.api}${path}`;
      try {
        const response = await axios({
          url,
          method,
          headers: await APIService.getDefaultHeaders({ headers, options }),
          params: method === "get" ? data : undefined,
          data: method !== "get" ? data : undefined,
          cancelToken,
        });

        const normalizedData = normalize(response.data, { endpoint: url });
        const pagination = normalizePagination(response.data.meta);

        resolve({
          ...(normalizeData ? normalizedData : response.data),
          pagination,
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          loggerService.log({
            icon: "❌",
            title: "API Call",
            body: "API call cancelled",
          });
          reject();
        } else {
          const apiError = error as IJSONAPIError;

          const cleanError: IAPIError = {
            ...omit(apiError.response, ["request", "headers", "config"]),
            errorCodes:
              get(apiError.response, "data.violations")?.map(
                (violation: IJSONAPIVialotion) => violation.code
              ) || [],
            propertyPaths:
              get(apiError.response, "data.violations")?.map(
                (violation: IJSONAPIVialotion) => violation.propertyPath
              ) || [],
          };

          loggerService.log({
            icon: "❌",
            title: "API Call",
            body: cleanError,
          });

          // remove all non-serializable data from error
          reject(cleanError);
        }
      }
    });

  static get = (args: IAPICallProps) => APIService.apiCall({ ...args, method: "get" });
  static post = ({ isJSON, ...args }: IAPICallProps) =>
    APIService.apiCall(
      merge(
        {},
        args,
        {
          method: "post",
        },
        isJSON && {
          data: args.data || {},
          headers: {
            "content-type": API_CONTENT_TYPE.JSON,
          },
        }
      )
    );
  static put = (args: IAPICallProps) => APIService.apiCall({ ...args, method: "put" });
  static patch = (args: IAPICallProps) =>
    APIService.apiCall({
      ...args,
      method: "patch",
      headers: getHeaders("PATCH"),
    });
  static delete = (args: IAPICallProps) => APIService.apiCall({ ...args, method: "delete" });
}
